import { Middleware, MiddlewareAPI } from 'redux';

import { RegexConst } from '@nocowanie/core/consts';

import { SliceNameEnum } from '@app/enums';
import { mapSearchStateToUrlParams } from '@app/helpers/search-params.helpers';
import {
    setPageAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
} from '@app/state/slices/search-state.actions';

// Checks if action is a search action with shouldUpdateUrl flag
function isSearchUpdateUrlAction(action: unknown): boolean {
    if (!action || typeof action !== 'object' || !('type' in action) || !('payload' in action)) {
        return false;
    }

    const { type, payload } = action;

    const isSearchAction =
        type === updateSearchCriteriaAction.type ||
        type === updateSearchFiltersAction.type ||
        type === setPageAction.type;

    const hasShouldUpdateUrlFlag =
        payload &&
        typeof payload === 'object' &&
        payload !== null &&
        'shouldUpdateUrl' in payload &&
        payload.shouldUpdateUrl === true;

    return Boolean(isSearchAction && hasShouldUpdateUrlFlag);
}

/**
 * Middleware to sync map search state with URL parameters.
 * Only updates the URL when actions include shouldUpdateUrl=true flag.
 */
export const searchUrlMiddleware: Middleware =
    (api: MiddlewareAPI) => next => (action: unknown) => {
        const result = next(action);

        if (typeof window === 'undefined') {
            return result;
        }

        if (isSearchUpdateUrlAction(action)) {
            const state = api.getState();
            const searchState = state[SliceNameEnum.Search];
            const { searchCriteria, filters, page } = searchState;

            const searchParams = mapSearchStateToUrlParams(searchCriteria, filters, page);

            // On location listing pages the route path already encodes the location,
            // so these params are redundant in the query string.
            const isCampaignListingPage = RegexConst.CAMPAIGN_PAGE_TYPE.test(
                window.location.pathname,
            );
            if (!isCampaignListingPage) {
                searchParams.delete('location');
                searchParams.delete('geoId');
                searchParams.delete('type');
            }

            const url = new URL(window.location.href);
            url.search = searchParams.toString();

            window.history.replaceState({}, '', url.toString());
        }

        return result;
    };
